import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

// Named themes. `light`/`dark` are the base palettes; anything else is a "skin"
// layered on top of the dark base (so Tailwind `dark:` variants keep working)
// via a scoped stylesheet keyed on its root class — see ghost.css. To add a
// theme: extend this union, THEMES, and THEME_ROOT_CLASS, then ship a
// `.theme-<name>` stylesheet.
export type Theme = 'light' | 'dark' | 'ghost' | 'newvegas';

export const THEMES: { value: Theme; label: string }[] = [
    { value: 'light', label: 'Luminos' },
    { value: 'dark', label: 'Intunecat' },
    { value: 'ghost', label: 'Ghost' },
    { value: 'newvegas', label: 'New Vegas' },
];

// Class(es) on the wrapper above `.v2-root`. Dark-based skins include `dark` so
// the existing dark: variants apply, plus their own `theme-*` hook.
const THEME_ROOT_CLASS: Record<Theme, string> = {
    light: '',
    dark: 'dark',
    ghost: 'dark theme-ghost',
    newvegas: 'dark theme-newvegas',
};
export const themeRootClass = (t: Theme): string => THEME_ROOT_CLASS[t];
// Coarse light/dark mode a theme reads as — for things that only understand the
// two (e.g. sonner's Toaster).
export const themeMode = (t: Theme): 'light' | 'dark' => (t === 'light' ? 'light' : 'dark');

const STORAGE_KEY = 'v2-theme';
const isTheme = (v: unknown): v is Theme => THEMES.some((t) => t.value === v);

interface ThemeContextValue {
    theme: Theme;
    setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function getInitialTheme(): Theme {
    if (typeof window === 'undefined') return 'light';
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (isTheme(stored)) return stored;
    return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function ThemeProvider({ children }: { children: ReactNode }) {
    const [theme, setTheme] = useState<Theme>(getInitialTheme);

    useEffect(() => {
        try {
            window.localStorage.setItem(STORAGE_KEY, theme);
        } catch {
            // localStorage may be unavailable (private mode, etc.) — non-fatal.
        }
    }, [theme]);

    const value = useMemo<ThemeContextValue>(() => ({ theme, setTheme }), [theme]);

    return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
    const ctx = useContext(ThemeContext);
    if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
    return ctx;
}
